import React, { useState, useCallback, useEffect } from 'react';
import { Upload, X, FileImage, Calculator, Send, CheckCircle, Package, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { unifiedAPI } from '../utils/api';

const QuickOrderModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    productType: '',
    selectedProduct: '',
    quantity: 25,
    logoFile: null,
    logoPreview: null,
    additionalNotes: ''
  });
  const [quote, setQuote] = useState(null);

  // Fetch categories when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchCategories = async () => {
        try {
          setLoadingData(true);
          const response = await unifiedAPI.getCategories({ product_type: 'awards', include_counts: true });
          setCategories(response.categories || []);
        } catch (error) {
          console.error('Error fetching categories:', error);
        } finally {
          setLoadingData(false);
        }
      };
      fetchCategories();
    }
  }, [isOpen]);

  // Fetch products when category changes
  useEffect(() => {
    if (formData.productType) {
      const fetchProducts = async () => {
        try {
          setLoadingData(true);
          const response = await unifiedAPI.getProducts({ 
            product_type: 'awards',
            category_id: formData.productType,
            limit: 50 
          });
          setProducts(response.products || []);
        } catch (error) {
          console.error('Error fetching products:', error);
        } finally {
          setLoadingData(false);
        }
      };
      fetchProducts();
    }
  }, [formData.productType]);

  const quantityTiers = [
    { min: 1, max: 24, discount: 0, label: '1-24 units' },
    { min: 25, max: 49, discount: 5, label: '25-49 units (5% off)' },
    { min: 50, max: 99, discount: 10, label: '50-99 units (10% off)' },
    { min: 100, max: 249, discount: 15, label: '100-249 units (15% off)' },
    { min: 250, max: 499, discount: 20, label: '250-499 units (20% off)' },
    { min: 500, max: Infinity, discount: 25, label: '500+ units (25% off)' }
  ];

  const handleFileUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          logoFile: file,
          logoPreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const removeLogo = () => {
    setFormData(prev => ({
      ...prev,
      logoFile: null,
      logoPreview: null
    }));
  };

  const calculateQuote = () => {
    const product = products.find(p => p.id === formData.selectedProduct);
    if (!product) return;

    const tier = quantityTiers.find(t => formData.quantity >= t.min && formData.quantity <= t.max);
    const basePrice = product.sizes?.[1]?.price || product.priceRange?.min || product.basePrice || 100;
    const discountPercent = tier?.discount || 0;
    const unitPrice = basePrice * (1 - discountPercent / 100);
    const subtotal = unitPrice * formData.quantity;
    const setupFee = formData.quantity >= 50 ? 0 : 50;
    const total = subtotal + setupFee;

    setQuote({
      product: product.name,
      quantity: formData.quantity,
      basePrice,
      discount: discountPercent,
      unitPrice,
      subtotal,
      setupFee,
      total,
      estimatedDays: formData.quantity > 100 ? '7-10' : '3-5'
    });
    setStep(3);
  };

  const handleSubmit = () => {
    setStep(4);
  };

  const resetForm = () => {
    setStep(1);
    setFormData({
      companyName: '',
      contactName: '',
      email: '',
      phone: '',
      productType: '',
      selectedProduct: '',
      quantity: 25,
      logoFile: null,
      logoPreview: null,
      additionalNotes: ''
    });
    setQuote(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="quick-order-modal">
      {/* Backdrop - Elegant blur */}
      <div 
        className="absolute inset-0 bg-ivory-900/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal - Luxury styling */}
      <Card className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-ivory-50 border border-ivory-300 shadow-luxury-lg">
        <CardHeader className="border-b border-ivory-200 sticky top-0 bg-ivory-50 z-10">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-heading text-ivory-800 flex items-center gap-2">
                <Package className="w-6 h-6 text-gold-600" />
                Quick Order - Bulk Quote
              </CardTitle>
              <p className="text-ivory-500 text-sm mt-1 font-body">Get an instant quote for your corporate awards</p>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose}
              className="text-ivory-500 hover:text-ivory-800 hover:bg-ivory-200"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center gap-2 mt-4">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center flex-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium font-body transition-colors ${
                  step >= s 
                    ? 'bg-gradient-to-r from-gold-600 to-gold-500 text-white' 
                    : 'bg-ivory-200 text-ivory-500'
                }`}>
                  {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                </div>
                {s < 4 && (
                  <div className={`flex-1 h-1 mx-2 rounded ${
                    step > s ? 'bg-gold-500' : 'bg-ivory-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-ivory-500 mt-1 px-1 font-body">
            <span>Details</span>
            <span>Product</span>
            <span>Quote</span>
            <span>Done</span>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {/* Step 1: Contact Details */}
          {step === 1 && (
            <div className="space-y-4" data-testid="step-1">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-ivory-700 mb-2 block font-body">Company Name *</Label>
                  <Input
                    value={formData.companyName}
                    onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                    placeholder="Your Company Inc."
                    className="bg-ivory-100 border-ivory-300 text-ivory-800 font-body"
                    data-testid="company-name-input"
                  />
                </div>
                <div>
                  <Label className="text-ivory-700 mb-2 block font-body">Contact Name *</Label>
                  <Input
                    value={formData.contactName}
                    onChange={(e) => setFormData(prev => ({ ...prev, contactName: e.target.value }))}
                    placeholder="John Smith"
                    className="bg-ivory-100 border-ivory-300 text-ivory-800 font-body"
                    data-testid="contact-name-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-ivory-700 mb-2 block font-body">Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="john@company.com"
                    className="bg-ivory-100 border-ivory-300 text-ivory-800 font-body"
                    data-testid="email-input"
                  />
                </div>
                <div>
                  <Label className="text-ivory-700 mb-2 block font-body">Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                    className="bg-ivory-100 border-ivory-300 text-ivory-800 font-body"
                  />
                </div>
              </div>
              
              <Button
                onClick={() => setStep(2)}
                disabled={!formData.companyName || !formData.contactName || !formData.email}
                className="w-full mt-4 btn-luxury-primary disabled:opacity-50 font-body"
                data-testid="next-step-btn"
              >
                Continue to Product Selection
              </Button>
            </div>
          )}

          {/* Step 2: Product & Logo */}
          {step === 2 && (
            <div className="space-y-5" data-testid="step-2">
              <div>
                <Label className="text-ivory-700 mb-2 block font-body">Award Category *</Label>
                <Select 
                  value={formData.productType} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, productType: v, selectedProduct: '' }))}
                >
                  <SelectTrigger className="bg-ivory-100 border-ivory-300 text-ivory-800 font-body" data-testid="category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-ivory-50 border-ivory-300">
                    {loadingData ? (
                      <div className="p-2 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
                    ) : (
                      categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id} className="text-ivory-700 hover:bg-ivory-100 font-body">
                          {cat.name} ({cat.count || 0} products)
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-ivory-700 mb-2 block font-body">Select Product *</Label>
                <Select 
                  value={formData.selectedProduct} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, selectedProduct: v }))}
                  disabled={!formData.productType}
                >
                  <SelectTrigger className="bg-ivory-100 border-ivory-300 text-ivory-800 font-body" data-testid="product-select">
                    <SelectValue placeholder={formData.productType ? "Choose an award" : "Select a category first"} />
                  </SelectTrigger>
                  <SelectContent className="bg-ivory-50 border-ivory-300 max-h-60">
                    {loadingData ? (
                      <div className="p-2 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
                    ) : (
                      products.map(prod => (
                        <SelectItem key={prod.id} value={prod.id} className="text-ivory-700 hover:bg-ivory-100 font-body">
                          {prod.name} (${prod.priceRange?.min?.toFixed(2) || prod.basePrice?.toFixed(2) || '0.00'})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-ivory-700 mb-2 block font-body">Quantity *</Label>
                <div className="flex gap-2 flex-wrap">
                  {[25, 50, 100, 250, 500].map(qty => (
                    <Button
                      key={qty}
                      type="button"
                      variant={formData.quantity === qty ? 'default' : 'outline'}
                      onClick={() => setFormData(prev => ({ ...prev, quantity: qty }))}
                      className={formData.quantity === qty 
                        ? 'bg-gold-600 hover:bg-gold-700 text-white font-body' 
                        : 'border-ivory-300 text-ivory-600 hover:bg-ivory-100 font-body'
                      }
                    >
                      {qty}
                    </Button>
                  ))}
                  <Input
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => setFormData(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                    className="w-24 bg-ivory-100 border-ivory-300 text-ivory-800 font-body"
                    data-testid="quantity-input"
                  />
                </div>
                <p className="text-xs text-ivory-500 mt-1 font-body">
                  {quantityTiers.find(t => formData.quantity >= t.min && formData.quantity <= t.max)?.label}
                </p>
              </div>

              {/* Logo Upload */}
              <div>
                <Label className="text-ivory-700 mb-2 block font-body">Upload Your Logo (Optional)</Label>
                {!formData.logoPreview ? (
                  <div 
                    className="border-2 border-dashed border-ivory-300 rounded-lg p-8 text-center hover:border-gold-500/50 transition-colors cursor-pointer bg-ivory-100/50"
                    onClick={() => document.getElementById('logo-upload').click()}
                  >
                    <Upload className="w-10 h-10 text-ivory-400 mx-auto mb-3" />
                    <p className="text-ivory-600 mb-1 font-body">Click to upload your logo</p>
                    <p className="text-xs text-ivory-500 font-body">PNG, JPG, SVG, AI, EPS (Max 10MB)</p>
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/*,.ai,.eps,.svg"
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="logo-upload-input"
                    />
                  </div>
                ) : (
                  <div className="relative border border-ivory-300 rounded-lg p-4 bg-ivory-100">
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-lg overflow-hidden bg-white flex items-center justify-center shadow-luxury-sm">
                        <img 
                          src={formData.logoPreview} 
                          alt="Logo preview" 
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-ivory-800 font-medium font-body">{formData.logoFile?.name}</p>
                        <p className="text-sm text-ivory-500 font-body">
                          {(formData.logoFile?.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={removeLogo}
                        className="text-ivory-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <X className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-ivory-700 mb-2 block font-body">Additional Notes</Label>
                <Textarea
                  value={formData.additionalNotes}
                  onChange={(e) => setFormData(prev => ({ ...prev, additionalNotes: e.target.value }))}
                  placeholder="Special requirements, engraving text ideas, deadline, etc."
                  className="bg-ivory-100 border-ivory-300 text-ivory-800 min-h-[80px] font-body"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1 btn-luxury-outline font-body"
                >
                  Back
                </Button>
                <Button
                  onClick={calculateQuote}
                  disabled={!formData.selectedProduct || !formData.quantity}
                  className="flex-1 btn-luxury-primary disabled:opacity-50 font-body"
                  data-testid="calculate-quote-btn"
                >
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculate Quote
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Quote Display */}
          {step === 3 && quote && (
            <div className="space-y-5" data-testid="step-3">
              <div className="gradient-champagne border border-gold-500/30 rounded-lg p-6">
                <h3 className="text-xl font-heading font-semibold text-ivory-800 mb-4 flex items-center gap-2">
                  <FileImage className="w-5 h-5 text-gold-600" />
                  Your Instant Quote
                </h3>
                
                <div className="space-y-3 font-body">
                  <div className="flex justify-between text-ivory-600">
                    <span>Product:</span>
                    <span className="text-ivory-800 font-medium">{quote.product}</span>
                  </div>
                  <div className="flex justify-between text-ivory-600">
                    <span>Quantity:</span>
                    <span className="text-ivory-800 font-medium">{quote.quantity} units</span>
                  </div>
                  <div className="flex justify-between text-ivory-600">
                    <span>Base Price per Unit:</span>
                    <span className="text-ivory-800">${quote.basePrice.toFixed(2)}</span>
                  </div>
                  {quote.discount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Volume Discount:</span>
                      <span>-{quote.discount}%</span>
                    </div>
                  )}
                  <div className="flex justify-between text-ivory-600">
                    <span>Price per Unit (after discount):</span>
                    <span className="text-ivory-800 font-medium">${quote.unitPrice.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-ivory-300 pt-3 mt-3">
                    <div className="flex justify-between text-ivory-600">
                      <span>Subtotal:</span>
                      <span className="text-ivory-800">${quote.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-ivory-600">
                      <span>Setup & Artwork Fee:</span>
                      <span className={quote.setupFee === 0 ? 'text-green-600' : 'text-ivory-800'}>
                        {quote.setupFee === 0 ? 'FREE' : `$${quote.setupFee.toFixed(2)}`}
                      </span>
                    </div>
                  </div>
                  <div className="border-t border-gold-500/30 pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-ivory-800">Estimated Total:</span>
                      <span className="text-3xl font-bold text-gold-600">${quote.total.toFixed(2)}</span>
                    </div>
                    <p className="text-sm text-ivory-500 mt-2">
                      Estimated production: {quote.estimatedDays} business days
                    </p>
                  </div>
                </div>
              </div>

              {formData.logoPreview && (
                <div className="bg-ivory-100 rounded-lg p-4 border border-ivory-300">
                  <p className="text-sm text-ivory-500 mb-2 font-body">Your uploaded logo:</p>
                  <div className="w-16 h-16 rounded bg-white flex items-center justify-center shadow-luxury-sm">
                    <img src={formData.logoPreview} alt="Logo" className="max-w-full max-h-full object-contain" />
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="flex-1 btn-luxury-outline font-body"
                >
                  Modify Order
                </Button>
                <Button
                  onClick={handleSubmit}
                  className="flex-1 btn-luxury-primary font-body"
                  data-testid="submit-quote-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Submit Quote Request
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Success */}
          {step === 4 && (
            <div className="text-center py-8" data-testid="step-4">
              <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-heading font-semibold text-ivory-800 mb-2">Quote Request Submitted!</h3>
              <p className="text-ivory-500 mb-6 font-body">
                Thank you, {formData.contactName}! We&apos;ve received your quote request for {formData.quantity} units of {quote?.product}.
              </p>
              <div className="bg-ivory-100 rounded-lg p-4 mb-6 text-left border border-ivory-200">
                <p className="text-sm text-ivory-500 font-body">What happens next:</p>
                <ul className="mt-2 space-y-2 text-sm text-ivory-600 font-body">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-gold-600 mt-0.5 flex-shrink-0" />
                    Our team will review your request within 1-2 business hours
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-gold-600 mt-0.5 flex-shrink-0" />
                    We&apos;ll send a detailed quote to {formData.email}
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-gold-600 mt-0.5 flex-shrink-0" />
                    A dedicated account manager will contact you for artwork approval
                  </li>
                </ul>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => { resetForm(); onClose(); }}
                  className="flex-1 btn-luxury-outline font-body"
                >
                  Close
                </Button>
                <Button
                  onClick={resetForm}
                  className="flex-1 btn-luxury-primary font-body"
                >
                  Create Another Quote
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default QuickOrderModal;
